/**
 * Plant days: the YYYY-MM-DD days a plant books its production on (the org's clock, #971). A day here is a calendar
 * day with no time of its own, so these helpers never go through a local Date: counting and formatting stay the same
 * in every browser and time zone.
 */

const MONTHS = ["Jan", "Feb", "Mar", "Apr", "May", "Jun", "Jul", "Aug", "Sep", "Oct", "Nov", "Dec"];

function parts(day: string): [number, number, number] {
  return [Number(day.slice(0, 4)), Number(day.slice(5, 7)), Number(day.slice(8, 10))];
}

/** True for a real calendar day written as YYYY-MM-DD (2026-02-29 is not one). */
export function isPlantDay(s: unknown): s is string {
  if (typeof s !== "string" || !/^\d{4}-\d{2}-\d{2}$/.test(s)) return false;
  const [y, m, d] = parts(s);
  if (y < 1000 || m < 1 || m > 12 || d < 1) return false;
  return d <= new Date(Date.UTC(y, m, 0)).getUTCDate();
}

/** Days since 1970-01-01, for counting between plant days. */
export function dayNumber(day: string): number {
  const [y, m, d] = parts(day);
  return Math.round(Date.UTC(y, m - 1, d) / 86_400_000);
}

/** "5 Aug 2026". */
export function formatDay(day: string): string {
  const [y, m, d] = parts(day);
  return `${d} ${MONTHS[m - 1]} ${y}`;
}

/** An instant's plant day on the UTC calendar, as the reports write their bounds, in formatDay's words; the text as it
 * is when it does not read (the 2026-09-28 walk, R6: "Jun 20, 2026" in a US browser beside "20 Jun 2026"). */
export function formatInstantDay(iso: string): string {
  const t = Date.parse(iso);
  return Number.isNaN(t) ? iso : formatDay(new Date(t).toISOString().slice(0, 10));
}

/** An instant as a stamp in the viewer's own time: "27 Sep 2026, 17:14"; the text as it is when it does not read. */
export function formatStamp(iso: string): string {
  const t = Date.parse(iso);
  if (Number.isNaN(t)) return iso;
  const d = new Date(t);
  const day = `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, "0")}-${String(d.getDate()).padStart(2, "0")}`;
  return `${formatDay(day)}, ${String(d.getHours()).padStart(2, "0")}:${String(d.getMinutes()).padStart(2, "0")}`;
}

/** A range as briefly as it reads: "1–31 Aug 2026", "28 Aug – 3 Sep 2026", "28 Dec 2025 – 3 Jan 2026", "5 Aug 2026". */
export function formatDayRange(from: string, to: string, opts?: { plain?: boolean }): string {
  if (from === to) return formatDay(from);
  const [fy, fm, fd] = parts(from);
  const [ty, tm, td] = parts(to);
  // In words, with no dash: "1 to 30 Sep 2026", "28 Aug to 3 Sep 2026", "28 Dec 2025 to 3 Jan 2026".
  if (opts?.plain) {
    if (fy === ty && fm === tm) return `${fd} to ${td} ${MONTHS[tm - 1]} ${ty}`;
    if (fy === ty) return `${fd} ${MONTHS[fm - 1]} to ${td} ${MONTHS[tm - 1]} ${ty}`;
    return `${formatDay(from)} to ${formatDay(to)}`;
  }
  if (fy === ty && fm === tm) return `${fd}–${td} ${MONTHS[tm - 1]} ${ty}`;
  if (fy === ty) return `${fd} ${MONTHS[fm - 1]} – ${td} ${MONTHS[tm - 1]} ${ty}`;
  return `${formatDay(from)} – ${formatDay(to)}`;
}
